import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
  { to: "/", label: "Home", id: "home" },
  { to: "/roster", label: "Roster", id: "roster" },
  { to: "/events", label: "Events", id: "events" },
  { to: "/championships", label: "Champions", id: "champions" },
  { to: "/news", label: "News", id: "news" },
  { to: "/gallery", label: "Gallery", id: "gallery" },
  { to: "/media", label: "Media", id: "media" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 w-full z-50 transition-colors duration-300 border-b ${
        scrolled ? "bg-[#050505]/90 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      <nav className="px-6 md:px-12 lg:px-20 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group shrink-0">
          <img src="https://customer-assets.emergentagent.com/job_wrestling-federation/artifacts/traed3wv_Yellow%20Logo.png" alt="Championship Wrestling Federation" className="h-14 w-auto" />
          <span data-testid="nav-wordmark" className="hidden md:flex lg:hidden min-[1600px]:flex flex-col justify-center leading-none gap-0.5 border-l border-white/15 pl-3 whitespace-nowrap">
            <span className="font-display text-[28px] leading-none tracking-wide text-white">Championship Wrestling</span>
            <span className="font-display text-[28px] leading-none tracking-wide text-brand">Federation</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-5 xl:gap-7">
          {links.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={`nav-${l.id}-link`}
              className={`font-display text-xl uppercase tracking-wide whitespace-nowrap transition-colors ${
                pathname === l.to ? "text-brand" : "text-zinc-300 hover:text-brand"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <Link
            to="/sponsors"
            data-testid="nav-sponsors-cta"
            className="bg-brand hover:bg-brand-hi text-black font-display text-xl uppercase tracking-wide whitespace-nowrap px-6 py-2 transition-colors"
          >
            Sponsors
          </Link>
          <Link to="/openring" data-testid="nav-openring-logo" aria-label="Open Ring" className="shrink-0 transition-transform duration-300 hover:scale-105">
            <img src="/openring/logo.png" alt="OPEN RING" className="h-8 xl:h-9 w-auto" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden text-white"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </nav>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#0a0a0a] border-t border-white/10 px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <Link key={l.id} to={l.to} data-testid={`mobile-nav-${l.id}-link`} className="font-display text-2xl uppercase text-zinc-200 hover:text-brand">
              {l.label}
            </Link>
          ))}
          <Link to="/sponsors" className="bg-brand text-black font-display text-2xl uppercase px-6 py-3 text-center mt-2">
            Sponsors
          </Link>
          <Link to="/openring" data-testid="mobile-nav-openring-logo" aria-label="Open Ring" className="flex justify-center border border-white/15 py-4">
            <img src="/openring/logo.png" alt="OPEN RING" className="h-9 w-auto" />
          </Link>
        </div>
      )}
    </header>
  );
}
